// Rechteckige, durchgehende Ausschnitte einer Platte — parametrisch in part.meta.cutouts.
// Koordinaten: Versatz des Ausschnitt-Mittelpunkts vom Teil-Mittelpunkt,
// x entlang der Länge, z entlang der Breite. Engine-frei.
//
// Bohrungen (shape "circle") kennen eine Bohrachse `axis`. Fehlt das Feld, gilt
// "y" — alle Altbestände bleiben damit unverändert gültig.
//
//   axis "y" (Flächenbohrung, Default): Bohrachse ⟂ Fläche, entlang der Stärke.
//     x_mm/z_mm = Lage auf der Fläche, side "top" = von oben (+y), "bottom" = von unten.
//     Ohne depth_mm (bzw. depth ≥ Stärke) ist die Bohrung durchgehend.
//
//   axis "x" (Stirnbohrung längs): Bohrachse entlang der Länge, Eintritt in eine
//     Stirnseite. side "top" = rechte Stirn (+x), "bottom" = linke Stirn (−x).
//     Lage: z_mm quer über die Breite, y_mm über die Stärke (0 = Mitte).
//
//   axis "z" (Stirnbohrung quer): Bohrachse entlang der Breite, Eintritt in eine
//     Schmalseite. side "top" = hintere Schmalseite (+z), "bottom" = vordere (−z).
//     Lage: x_mm entlang der Länge, y_mm über die Stärke (0 = Mitte).
//
// Stirnbohrungen sind immer Sacklöcher: depth_mm wird garantiert gesetzt (≤ Teil-
// Ausdehnung entlang der Bohrachse minus Reststeg) — sie tauchen deshalb nie als
// Loch in der Flächen-Geometrie auf.

const MIN_SIZE = 5       // mm
const EDGE = 1           // Mindest-Reststeg zur Kante, verhindert kaputte Triangulation
const MIN_DEPTH = 1      // mm
const DEFAULT_DEPTH = 30 // Stirnbohrung ohne brauchbare Tiefe (z. B. Achswechsel)

export function cutoutsOf(part) {
  return part.meta?.cutouts ?? []
}

export function withCutouts(part, cutouts) {
  return { ...part.meta, cutouts }
}

export function isCircle(cutout) {
  return cutout.shape === "circle"
}

// Bohrachse einer Bohrung; alles andere (und fehlende Angabe) ist "y".
export function cutoutAxis(cutout) {
  if (!isCircle(cutout)) return "y"
  return cutout.axis === "x" || cutout.axis === "z" ? cutout.axis : "y"
}

// Stirnbohrung = Bohrachse liegt in der Fläche (Eintritt in eine Schmalseite)
export function isEndGrain(cutout) {
  return isCircle(cutout) && cutoutAxis(cutout) !== "y"
}

// Durchgehend = alles außer Sackloch-Bohrungen (Stirnbohrungen nie)
export function isThrough(cutout) {
  return !(isCircle(cutout) && (cutout.depth_mm || isEndGrain(cutout)))
}

// Teil-Ausdehnungen entlang der Bohrachse und ihrer beiden Querachsen
function endGrainExtents(axis, part) {
  return axis === "x"
    ? { along: part.length_mm, cross: part.width_mm, crossKey: "z_mm" }
    : { along: part.width_mm, cross: part.length_mm, crossKey: "x_mm" }
}

// Stirnbohrung: Tiefe ≤ Ausdehnung entlang der Bohrachse minus Reststeg,
// Querlage (quer + über die Stärke) innerhalb der Teilgrenzen.
function clampEndGrain(cutout, part, axis) {
  const { along, cross, crossKey } = endGrainExtents(axis, part)
  const maxD = Math.min(cross, part.thickness_mm) - 2 * EDGE
  const diameter_mm = Math.round(clamp(cutout.diameter_mm, MIN_SIZE, maxD))
  const maxDepth = along - EDGE
  const rawDepth = Number(cutout.depth_mm)
  const depth = Number.isFinite(rawDepth) && rawDepth > 0 ? rawDepth : Math.min(DEFAULT_DEPTH, maxDepth)
  const maxCross = (cross - diameter_mm) / 2 - EDGE
  const maxY = (part.thickness_mm - diameter_mm) / 2 - EDGE
  const result = {
    shape: "circle",
    axis,
    diameter_mm,
    depth_mm: Math.round(clamp(depth, MIN_DEPTH, maxDepth)),
    side: cutout.side === "bottom" ? "bottom" : "top",
    [crossKey]: Math.round(clamp(cutout[crossKey] ?? 0, -maxCross, maxCross)),
    y_mm: Math.round(clamp(cutout.y_mm ?? 0, -maxY, maxY))
  }
  return withExtras(result, cutout)
}

// Nicht-geometrische Zusatzfelder (source, joint_id, …) überleben das Klemmen.
// Ohne das verlöre z. B. eine Verbinder-Bohrung beim Resize ihre Stoß-
// Zuordnung und der Assistent könnte die Verbindung nicht mehr erneuern.
const GEOMETRY_KEYS = [ "shape", "axis", "diameter_mm", "length_mm", "width_mm",
                        "x_mm", "y_mm", "z_mm", "depth_mm", "side" ]

function withExtras(result, cutout) {
  for (const [ key, value ] of Object.entries(cutout)) {
    if (!GEOMETRY_KEYS.includes(key) && value !== undefined) result[key] = value
  }
  return result
}

// Begrenzt einen Ausschnitt/eine Bohrung so, dass sie vollständig in der Platte liegt.
// Rechteckige Ausschnitte tragen kein shape-Feld (Rückwärtskompatibilität).

export function clampCutout(cutout, part) {
  if (isEndGrain(cutout)) return clampEndGrain(cutout, part, cutoutAxis(cutout))

  if (isCircle(cutout)) {
    const maxD = Math.min(part.length_mm, part.width_mm) - 2 * EDGE
    const diameter_mm = Math.round(clamp(cutout.diameter_mm, MIN_SIZE, maxD))
    const maxX = (part.length_mm - diameter_mm) / 2 - EDGE
    const maxZ = (part.width_mm - diameter_mm) / 2 - EDGE
    const result = {
      shape: "circle",
      diameter_mm,
      x_mm: Math.round(clamp(cutout.x_mm ?? 0, -maxX, maxX)),
      z_mm: Math.round(clamp(cutout.z_mm ?? 0, -maxZ, maxZ))
    }
    // Sackloch: Tiefe < Stärke; Tiefe >= Stärke oder 0/leer = durchgehend
    const depth = Number(cutout.depth_mm)
    if (Number.isFinite(depth) && depth > 0 && depth < part.thickness_mm) {
      result.depth_mm = Math.round(depth)
      result.side = cutout.side === "bottom" ? "bottom" : "top"
    }
    return withExtras(result, cutout)
  }

  const length_mm = Math.round(clamp(cutout.length_mm, MIN_SIZE, part.length_mm - 2 * EDGE))
  const width_mm = Math.round(clamp(cutout.width_mm, MIN_SIZE, part.width_mm - 2 * EDGE))
  const maxX = (part.length_mm - length_mm) / 2 - EDGE
  const maxZ = (part.width_mm - width_mm) / 2 - EDGE
  const result = {
    length_mm,
    width_mm,
    x_mm: Math.round(clamp(cutout.x_mm ?? 0, -maxX, maxX)),
    z_mm: Math.round(clamp(cutout.z_mm ?? 0, -maxZ, maxZ))
  }
  return withExtras(result, cutout)
}

// Werkstatt-Anzeige: Ausschnitte werden ab linker/vorderer Plattenkante zur
// Ausschnittkante vermessen, Bohrungen ab Kante zur Bohrungsmitte.
// Intern bleibt der Mittelpunkt-Versatz gespeichert.
export function toEdgeDistance(cutout, part, axis) {
  const partSize = axis === "x" ? part.length_mm : part.width_mm
  const center = axis === "x" ? cutout.x_mm : cutout.z_mm
  if (isCircle(cutout)) return center + partSize / 2
  const cutSize = axis === "x" ? cutout.length_mm : cutout.width_mm
  return center + (partSize - cutSize) / 2
}

export function fromEdgeDistance(value, cutout, part, axis) {
  const partSize = axis === "x" ? part.length_mm : part.width_mm
  if (isCircle(cutout)) return value - partSize / 2
  const cutSize = axis === "x" ? cutout.length_mm : cutout.width_mm
  return value - (partSize - cutSize) / 2
}

function clamp(value, min, max) {
  const number = Number.isFinite(value) ? value : 0
  return Math.min(Math.max(number, min), Math.max(min, max))
}
